import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useMemo, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {attachArchivePurpose} from 'archive/ArchivePurposesUtil';
import {assert, assertAndContinue} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {ProcessesFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcessExecutionType, ProcessTaskStatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  FetchSelectorsByIdQuery,
  FetchSelectorsByIdQueryVariables,
} from 'notebook/structured-tables/hooks/__generated/FetchSelectorsByIdQuery';
import {
  FETCH_SELECTORS_BY_ID_QUERY,
  getSelectorsById,
  SchemaFieldSelectorsDict,
} from 'notebook/structured-tables/hooks/useSelectorsById';
import ToggleEditStructuredTableOutsideNotebook from 'notebook/structured-tables/ToggleEditStructuredTableOutsideNotebook';
import {getSourceTypeForStructuredTable} from 'notebook/structured-tables/util/StructuredTableAnalyticsUtil';
import doesEvaluatedTableHaveAnyErrors from 'notebook/tables/errors/doesEvaluatedTableHaveAnyErrors';
import {EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {StructuredTableViewPanel} from 'notebook/tables/table-views/TableViewPanel';
import {CreateOrUpdateServerProcessOutput} from 'processes/dao/ProcessManagementDAO';
import {asyncBulkUpdateProcessWithDataMods} from 'processes/dashboard/bulk-edit/ProcessTaskEditorDropdown';
import {submitOutputs} from 'processes/process-details/submitOutputs';
import {EVALUATED_TABLE_ERROR_OPTIONS, ProcessTableLocation} from 'processes/ProcessConstants';
import {canEditSchemaFieldValues} from 'processes/processes-platform/FeatureOptions';
import ProcessRealtimeUpdateSubscriptionWrapper from 'processes/ProcessRealtimeUpdateSubscriptionWrapper';
import {getStatusFromId, isTerminalStatus} from 'processes/status/TaskStatusUtil';
import {createOutputTableWithTask} from 'processes/tables/createOutputTable';
import {outputTableToOutputsForCreateOrUpdate} from 'processes/tables/outputTableToOutputsUtils';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import submitOutputsWithConfirmation from 'processes/tables/submitOutputsWithConfirmation';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import pick from 'util/pick';

import {SchemaFieldSelectorGraphQL} from '../../../graphql-fragments/schemas/__generated/SchemaFieldSelectorGraphQL';
import {
  ProcessTaskOutputsQuery,
  ProcessTaskOutputsQueryVariables,
} from './graphql/__generated/ProcessTaskOutputsQuery';
import {PROCESS_TASK_OUTPUTS_QUERY} from './graphql/GetProcessTask';
import {updateProcessTaskDetailsOutputsQueryCache} from './utils/updateTaskDetailsOutputs';
import {updateProcessTaskDetailsQueryCache} from './utils/updateTaskDetailsTask';

type ProcessTask = ExtractByTypename<ProcessTaskOutputsQuery['processTask'], 'ProcessTaskNode'>;

interface QueryingProcessTaskOutputProps {
  processTaskId: string;
}

interface ProcessTaskOutputsProps {
  processTask: ProcessTask;
  selectors: Array<SchemaFieldSelectorGraphQL>;
  refetch: () => Promise<unknown>;
}

function shouldLockTable(processTask: ProcessTask): boolean {
  const isPending =
    getStatusFromId(processTask.status.id, processTask.schema.statusLifecycle)?.statusType ===
    ProcessTaskStatusType.PENDING;

  const editDisabled =
    !isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}) ||
    !canEditSchemaFieldValues(processTask.schema);
  // Covers edge case where a task is terminal without any outputs. By default, the outputs table
  // shows a single row for tasks like this. The row should be locked to prevent the creation
  // of any new outputs.
  const isTerminalWithNoOutputs =
    processTask.outputs.length === 0 &&
    isTerminalStatus(processTask.status.id, processTask.schema.statusLifecycle);
  return isPending || editDisabled || isTerminalWithNoOutputs;
}

function shouldDisallowAddRows(processTask: ProcessTask): boolean {
  return (
    isTerminalStatus(processTask.status.id, processTask.schema.statusLifecycle) ||
    !isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}) ||
    !canEditSchemaFieldValues(processTask.schema)
  );
}

function buildOutputTable(
  processTask: ProcessTask,
  selectorsById: SchemaFieldSelectorsDict
): ProcessOutputStructuredTable {
  return createOutputTableWithTask({
    taskId: processTask.id,
    outputs: processTask.outputs,
    processTaskSchema: {...processTask.schema, outputSchema: processTask.schema.outputSchema!},
    selectorsById,
    metadata: {
      tableLocation: ProcessTableLocation.TASK_DETAILS_OUTPUTS_TAB,
      disallowAddRows: shouldDisallowAddRows(processTask),
    },
    locked: shouldLockTable(processTask),
  });
}

function outputTableToOutputs(params: {
  table: ProcessOutputStructuredTable;
  evaluatedTable: EvaluatedTable;
  processTaskId: string;
  sourceType: string;
  creationOriginId: string;
  actionOrigin: string;
  actionMethod: string;
}): Array<CreateOrUpdateServerProcessOutput> {
  const {table, evaluatedTable, sourceType, creationOriginId, processTaskId, actionOrigin, actionMethod} =
    params;
  const outputs = outputTableToOutputsForCreateOrUpdate({
    table,
    evaluatedTable,
    // Preemptively fill more than necessary to prevent possible out-of-bounds error.
    // In reality, this should only be (the number of rows - outputs to update).
    creationOriginIds: Array(evaluatedTable.length).fill(creationOriginId),
    taskIds: Array(evaluatedTable.length).fill(processTaskId),
    sourceType,
    actionOrigin,
    actionMethod,
  });
  assertAndContinue(outputs.length > 0, `Expected outputs to submit to be greater than 0.`);
  return outputs;
}

export async function submitOutputTableHandler({
  processTask,
  outputTable,
  computedOutputTable,
  setOutputTable,
}: {
  processTask: ProcessTask;
  outputTable: ProcessOutputStructuredTable;
  computedOutputTable: EvaluatedTable;
  setOutputTable: (table: ProcessOutputStructuredTable) => void;
}): Promise<void> {
  const sourceType = `task_details_outputs_tab_${getSourceTypeForStructuredTable(outputTable)}_direct`;
  const actionOrigin = ActionOrigin.WORKFLOWS_TASK_DETAILS_PAGE;
  const actionMethod = ActionMethod.BUTTON_CLICK;
  assert((outputTable.columns?.length ?? 0) > 1, 'Output table must exist and have columns');
  if (doesEvaluatedTableHaveAnyErrors(computedOutputTable, EVALUATED_TABLE_ERROR_OPTIONS)) {
    return;
  }

  const outputs = outputTableToOutputs({
    table: outputTable,
    evaluatedTable: computedOutputTable,
    processTaskId: processTask.id,
    sourceType,
    creationOriginId: processTask.process.id,
    actionOrigin,
    actionMethod,
  });

  await submitOutputsWithConfirmation({
    outputs,
    onRequestSubmit: async ({processOutputs, processTasks}) => {
      await asyncBulkUpdateProcessWithDataMods(
        outputs.flatMap((output) => (output.id ? output.id : [])),
        async (dataModsHeader?: DataModificationsHeader) => {
          const {newOutputTable, serverOutputs} = await submitOutputs({
            processOutputs,
            processTasks: processTasks?.map((task) => ({...task, sourceType, actionOrigin, actionMethod})),
            outputTable,
            dataModsHeader,
          });
          setOutputTable(newOutputTable);
          const updatedStatus =
            processTasks != null && processTasks.length > 0
              ? getStatusFromId(processTasks[0].statusId, processTask.schema.statusLifecycle)
              : null;
          updateProcessTaskDetailsOutputsQueryCache({
            taskId: processTask.id,
            updatedStatusAndOutputs: {
              ...(updatedStatus != null ? {status: pick(updatedStatus, '__typename', 'id')} : {}),
              outputs: serverOutputs,
            },
          });
          // Keep status in sync with the details tab.
          updateProcessTaskDetailsQueryCache({
            id: processTask.id,
            ...(updatedStatus != null ? {status: updatedStatus} : {}),
          });
        },
        ActionOrigin.WORKFLOWS_TASK_DETAILS_PAGE
      );
    },
  });
}

function ProcessTaskOutputs(props: ProcessTaskOutputsProps): JSX.Element {
  const {processTask, selectors} = props;

  const selectorsById = useMemo(() => getSelectorsById(selectors), [selectors]);

  const enableEditsOnOutputTable = processTask.schema.executionType === ProcessExecutionType.DIRECT;
  const [outputTable, setOutputTable] = useState(buildOutputTable(processTask, selectorsById));
  const [computedOutputTable, setComputedOutputTable] = useState<EvaluatedTable | null>();

  useEffect(() => {
    setOutputTable(buildOutputTable(processTask, selectorsById));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processTask]);

  const updateHandlerOutputTable = (
    structuredTable: ProcessOutputStructuredTable,
    lastSyncedArchivePurposeByRow?: Array<string | null | undefined>
  ): void => {
    setOutputTable(structuredTable);
    if (lastSyncedArchivePurposeByRow) {
      const outputsWithArchivePurpose = processTask.outputs.map((output, idx) =>
        attachArchivePurpose(output, lastSyncedArchivePurposeByRow[idx])
      );
      updateProcessTaskDetailsOutputsQueryCache({
        taskId: processTask.id,
        updatedStatusAndOutputs: {
          outputs: outputsWithArchivePurpose,
        },
      });
    }
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_OUTPUT_TABLE_EDIT, {
      process_client_origin: processTask.schema.clientOrigin,
    });
  };

  return (
    <ToggleEditStructuredTableOutsideNotebook
      isEditable={enableEditsOnOutputTable}
      structuredTable={outputTable}
      disableMaximizedTables={false}
      outsideNotebookContext={{taskIds: [processTask.id]}}
      update={updateHandlerOutputTable}
      submit={async () => {
        await submitOutputTableHandler({
          processTask,
          outputTable,
          computedOutputTable: computedOutputTable!,
          setOutputTable,
        });
      }}
      revert={(table) => {
        setOutputTable(table);
      }}
      updateComputedTable={setComputedOutputTable}
      enableFormulas={true}
      useWithTableViews={true}
    />
  );
}

export default function QueryingProcessTaskOutputs(
  props: QueryingProcessTaskOutputProps
): JSX.Element | null {
  return (
    <BenchlingQuery<FetchSelectorsByIdQuery, FetchSelectorsByIdQueryVariables>
      query={FETCH_SELECTORS_BY_ID_QUERY}
      variables={{includeArchived: true}}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(selectorQueryData) => (
        <BenchlingQuery<ProcessTaskOutputsQuery, ProcessTaskOutputsQueryVariables>
          query={PROCESS_TASK_OUTPUTS_QUERY}
          variables={{taskId: props.processTaskId}}
          fetchPolicy="cache-and-network"
          renderCachedDataWhenRefetching={true}
          renderLoading={() => <CenteredHorizontalThrobber />}
        >
          {(data, result) => {
            const {processTask} = data;
            return !isInaccessible(processTask) ? (
              <ProcessRealtimeUpdateSubscriptionWrapper
                apiId={processTask.id}
                refetch={result.refetch}
                messageFilter="no_filter"
              >
                <div className={css(styles.structuredTableViewPanelWrapper)}>
                  <StructuredTableViewPanel>
                    <div className={classNames(css(styles.processTaskOutputsWrapper), 'scrollbar-override')}>
                      <ProcessTaskOutputs
                        processTask={processTask}
                        selectors={selectorQueryData.schemaFieldSelectors}
                        refetch={result.refetch}
                      />
                    </div>
                  </StructuredTableViewPanel>
                </div>
              </ProcessRealtimeUpdateSubscriptionWrapper>
            ) : (
              <WorkspaceItemInaccessibleGuard
                inaccessibleInfo={processTask}
                modelType={ModelTypeConstants.PROCESS_TASK}
              />
            );
          }}
        </BenchlingQuery>
      )}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  structuredTableViewPanelWrapper: {
    height: '100%',
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
  },
  processTaskOutputsWrapper: {
    height: '100%',
    overflow: 'auto',
    padding: '30px 20px',
  },
});
